import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import ProUpsell from '@/components/ProUpsell';
import Seo from '@/components/Seo';
import { toast, Toaster } from 'sonner';
import { Landmark, Info } from 'lucide-react';

const gbp = (n) => `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// HMRC VAT stagger groups — the months a quarter ENDS on, plus the group name.
const STAGGERS = {
  A: { group: 'Group A', ends: [3, 6, 9, 12], endLabel: 'Mar · Jun · Sep · Dec' },
  B: { group: 'Group B', ends: [4, 7, 10, 1], endLabel: 'Apr · Jul · Oct · Jan' },
  C: { group: 'Group C', ends: [5, 8, 11, 2], endLabel: 'May · Aug · Nov · Feb' },
};

const fmt = (y, m, d) => `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
const niceDate = (dt) => `${dt.getDate()} ${MONTHS[dt.getMonth()]} ${dt.getFullYear()}`;

function buildQuarters(ends, count = 6) {
  const today = new Date();
  const list = [];
  for (let y = today.getFullYear() + 1; y >= today.getFullYear() - 3; y--) {
    for (let i = ends.length - 1; i >= 0; i--) {
      const endMonth = ends[i]; // 1-indexed
      const endLast = new Date(y, endMonth, 0); // last day of endMonth
      const startDate = new Date(y, endMonth - 3, 1); // first day, 2 months before
      if (startDate > today) continue; // quarter not started yet
      // HMRC: return & payment due 1 calendar month + 7 days after quarter end = 7th of (endMonth + 2)
      const deadline = new Date(endLast.getFullYear(), endLast.getMonth() + 2, 7);
      list.push({
        key: `${y}-${endMonth}`,
        from: fmt(startDate.getFullYear(), startDate.getMonth() + 1, 1),
        to: fmt(endLast.getFullYear(), endLast.getMonth() + 1, endLast.getDate()),
        label: `${MONTHS[startDate.getMonth()]} ${startDate.getFullYear()} – ${MONTHS[endLast.getMonth()]} ${endLast.getFullYear()}`,
        deadline,
        deadlineLabel: niceDate(deadline),
        current: today >= startDate && today <= endLast,
      });
      if (list.length >= count) return list;
    }
  }
  return list;
}

export default function VatReturnPage() {
  const { user } = useAuth();
  const isPro = user?.is_pro || user?.role === 'admin';
  const [jobs, setJobs] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stagger, setStagger] = useState(() => {
    const raw = localStorage.getItem('tm_vat_stagger');
    const migrate = { '1': 'A', '2': 'B', '3': 'C' };
    const v = migrate[raw] || raw;
    return STAGGERS[v] ? v : 'A';
  });

  useEffect(() => {
    if (!isPro) { setLoading(false); return; }
    Promise.all([api.get('/jobs'), api.get('/receipts')])
      .then(([j, r]) => { setJobs(j.data); setReceipts(r.data); })
      .catch((e) => { if (e.response?.status !== 402) toast.error(formatApiError(e.response?.data?.detail)); })
      .finally(() => setLoading(false));
  }, [isPro]);

  const setStag = (s) => { setStagger(s); localStorage.setItem('tm_vat_stagger', s); };

  const unattached = useMemo(() => receipts.filter((r) => !r.attached), [receipts]);

  const quarters = useMemo(() => {
    const qs = buildQuarters(STAGGERS[stagger].ends);
    const jobDate = (j) => String(j.start_date || j.created_at || '').slice(0, 10);
    const recDate = (r) => String(r.date || r.created_at || '').slice(0, 10);
    return qs.map((q) => {
      const inQ = jobs.filter((j) => { const d = jobDate(j); return d && d >= q.from && d <= q.to; });
      const recQ = unattached.filter((r) => { const d = recDate(r); return d && d >= q.from && d <= q.to; });
      const output = inQ.reduce((s, j) => s + Number(j.vat_amount || 0), 0);
      const jobInput = inQ.reduce((s, j) => s + Number(j.input_vat || 0), 0);
      const looseInput = recQ.reduce((s, r) => s + Number(r.vat_amount || 0), 0);
      const input = jobInput + looseInput;
      const netSales = inQ.reduce((s, j) => s + Number(j.net_price || 0), 0);
      return { ...q, output, input, looseInput, looseCount: recQ.length, net: output - input, netSales, jobs: inQ.length };
    });
  }, [jobs, unattached, stagger]);

  if (!isPro) return <ProUpsell feature="VAT return view" testIdSuffix="vat" tier="pro" />;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Seo title="VAT Return" noindex />
      <Toaster position="top-center" richColors />
      <div className="mb-8">
        <p className="text-xs uppercase tracking-widest text-brand font-semibold">VAT return prep</p>
        <h1 className="font-display font-semibold text-4xl sm:text-5xl uppercase tracking-tight mt-1 flex items-center gap-3"><Landmark size={40} className="text-brand"/> VAT Return</h1>
        <p className="text-slate-600 mt-2">Your jobs split into HMRC VAT quarters. The <strong>Net VAT owed</strong> column is your Box 5 figure to pay each quarter.</p>
      </div>

      <div className="tm-card p-4 mb-6" data-testid="vat-stagger-picker">
        <span className="text-xs uppercase tracking-widest text-slate-500 block mb-3 font-semibold">Your HMRC stagger group — pick the one you're assigned to:</span>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {Object.entries(STAGGERS).map(([k, v]) => (
            <button key={k} onClick={() => setStag(k)} data-testid={`vat-stagger-${k}`}
              className={`text-left p-3 border transition-colors ${stagger === k ? 'bg-slate-900 text-white border-slate-800' : 'bg-white text-slate-700 border-slate-300 hover:border-slate-800'}`}>
              <span className="font-display font-semibold uppercase tracking-tight">{v.group}</span>
              <span className={`block text-xs font-mono mt-0.5 ${stagger === k ? 'text-slate-300' : 'text-slate-500'}`}>Quarters end {v.endLabel}</span>
            </button>
          ))}
        </div>
        <p className="text-[11px] text-slate-400 mt-3">Not sure which group you're in? Check your <a href="https://www.gov.uk/log-in-register-hmrc-online-services" target="_blank" rel="noreferrer" className="text-brand underline">HMRC VAT online account</a>. Return &amp; payment are due 1 month and 7 days after each quarter ends.</p>
      </div>

      {loading ? <p className="font-mono text-slate-400">Loading…</p> : (
        <div className="tm-card overflow-hidden" data-testid="vat-quarters-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-900 text-white text-[11px] uppercase tracking-wider font-semibold">
                  <th className="text-left p-3">Quarter</th>
                  <th className="text-right p-3">Net sales (ex VAT)</th>
                  <th className="text-right p-3">VAT on sales</th>
                  <th className="text-right p-3">VAT on costs</th>
                  <th className="text-right p-3">Net VAT owed <span className="text-brand">(Box 5)</span></th>
                  <th className="text-right p-3">Due by</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {quarters.map((q) => (
                  <tr key={q.key} className={q.current ? 'bg-orange-50' : ''} data-testid={`vat-quarter-${q.key}`}>
                    <td className="p-3">
                      <div className="font-bold">{q.label}{q.current && <span className="ml-2 text-[10px] uppercase bg-brand text-white px-1.5 py-0.5 font-semibold">Current</span>}</div>
                      <div className="text-[11px] font-mono text-slate-400">{q.jobs} job{q.jobs === 1 ? '' : 's'}{q.looseCount > 0 ? ` · ${q.looseCount} loose receipt${q.looseCount === 1 ? '' : 's'}` : ''}</div>
                    </td>
                    <td className="p-3 text-right font-mono">{gbp(q.netSales)}</td>
                    <td className="p-3 text-right font-mono text-slate-600">{gbp(q.output)}</td>
                    <td className="p-3 text-right font-mono text-slate-600" title={q.looseInput > 0 ? `Incl. ${gbp(q.looseInput)} from unattached receipts` : undefined}>
                      −{gbp(q.input)}{q.looseInput > 0 && <span className="text-brand">*</span>}
                    </td>
                    <td className={`p-3 text-right font-display font-semibold text-lg ${q.net >= 0 ? 'text-slate-900' : 'text-emerald-600'}`}>{gbp(q.net)}</td>
                    <td className="p-3 text-right font-mono text-xs text-slate-500 whitespace-nowrap">{q.deadlineLabel}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-6 flex items-start gap-2 text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded p-4">
        <Info size={16} className="shrink-0 mt-0.5 text-slate-400" />
        <div>
          <p><strong>How this is worked out:</strong> jobs are placed in a quarter by their start date; unattached receipts by their receipt date. VAT on sales is the VAT within your quoted price; VAT on costs is the reclaimable VAT from job costs <em>plus</em> any receipts sitting in your inbox that aren't attached to a job yet.</p>
          <p className="mt-1"><span className="text-brand font-bold">*</span> includes reclaimable VAT from unattached receipts. Manage them on the <Link to="/receipts" className="text-brand underline">Receipts</Link> page. This is a prep aid, not a substitute for filing through MTD-compatible software.</p>
        </div>
      </div>
    </div>
  );
}
